import React from "react";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Print as PrintIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../theme";
import { type ShiftTask } from "@/logic/api";



interface TaskListItemProps {
    task: ShiftTask;
    onEdit: (task: ShiftTask) => void;
    onDelete: (task: ShiftTask) => void;
    onPrint: (task: ShiftTask) => void;
    disableEdit?: boolean;
    disableDelete?: boolean;
    disablePrint?: boolean;
    isPrinting?: boolean;
}

export const TaskListItem: React.FC<TaskListItemProps> = ({
    task,
    onEdit,
    onDelete,
    onPrint,
    disableEdit = false,
    disableDelete = false,
    disablePrint = false,
    isPrinting = false,
}) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "white",
                transition: "all 0.2s ease",
                "&:hover": {
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)"
                },
            }}
            data-testid={`task-item-${task.title}`}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                >
                    {task.title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                >
                    {task.key}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    transition: "opacity 0.2s ease"
                }}
            >
                <Tooltip title={t("task.actions.printTask")}>
                    <IconButton
                        onClick={() => onPrint(task)}
                        disabled={disablePrint || isPrinting}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            "&:hover": {
                                color: theme.palette.primary.main,
                                bgcolor: "rgba(63, 81, 181, 0.1)",
                            },
                        }}
                        data-testid={`print-task-${task.id}`}
                    >
                        {isPrinting ? <CircularProgress size={20} color="inherit" /> : <PrintIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>

                <Tooltip title={t("task.actions.editTask")}>
                    <IconButton
                        onClick={() => onEdit(task)}
                        disabled={disableEdit}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            "&:hover": {
                                color: theme.palette.primary.main,
                                bgcolor: "rgba(63, 81, 181, 0.1)"
                            }
                        }}
                        data-testid={`edit-task-${task.id}`}
                    >
                        <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title={t("task.actions.deleteTask")}>
                    <IconButton
                        onClick={() => onDelete(task)}
                        disabled={disableDelete}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            "&:hover": {
                                color: "#ef4444",
                                bgcolor: "rgba(239, 68, 68, 0.1)"
                            },
                            "&:disabled": {
                                color: "#ccc"
                            }
                        }}
                        data-testid={`delete-task-${task.id}`}
                    >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};
